// Pure mathematical and computational functions for audio processing
// These functions have no side effects and are 100% testable

export interface FrameInfo {
  length: number;
  isValid: boolean;
  error?: string;
}

export interface PlayOptionsValidation {
  isValid: boolean;
  errors: string[];
}

export interface BeatPositionInfo {
  offset: number;
  adjustedStartTime: number;
}

/**
 * Parse an AAC frame header and return frame length
 * @param data - The audio data containing the frame
 * @param offset - The offset in the data where the frame starts
 * @returns Frame information including length and validity
 */
export function parseAACFrame(data: Uint8Array, offset: number): FrameInfo {
  // Check if we have enough bytes for a header
  if (offset + 5 >= data.length) {
    return {
      length: 0,
      isValid: false,
      error: 'Insufficient data for frame header'
    };
  }

  // Check sync word (0xFFF)
  if (data[offset] !== 0xff || data[offset + 1] >> 4 !== 0x0f) {
    return {
      length: 0,
      isValid: false,
      error: 'Invalid sync word'
    };
  }

  // Check MPEG version (must be MPEG-4)
  if ((data[offset + 2] & 0x03) >> 1 !== 0) {
    return {
      length: 0,
      isValid: false,
      error: 'Unexpected MPEG version'
    };
  }

  // Check protection bit
  if ((data[offset + 1] & 0x01) !== 1) {
    return {
      length: 0,
      isValid: false,
      error: 'Unexpected protection flag'
    };
  }

  // Check sample rate index (must be 3 for 48kHz)
  if ((data[offset + 2] & 0x3c) >> 2 !== 3) {
    return {
      length: 0,
      isValid: false,
      error: 'Unexpected sample rate'
    };
  }

  // Calculate frame length from header
  const frameLength =
    ((data[offset + 3] & 0x03) << 11) | (data[offset + 4] << 3) | (data[offset + 5] >> 5);

  return {
    length: frameLength,
    isValid: true
  };
}

/**
 * Calculate beat position offset for synchronized playback
 * @param globalStartTime - The global start time reference
 * @param currentTime - Current audio context time
 * @param playbackTempo - Current playback tempo in BPM
 * @param timeSignature - Time signature (beats per measure)
 * @param trackTimeSignature - The track's time signature (may differ from global)
 * @returns Beat position information
 */
export function calculateBeatPosition(
  globalStartTime: number | null,
  currentTime: number,
  playbackTempo: number,
  timeSignature: number,
  trackTimeSignature: number | null
): BeatPositionInfo {
  // If no global start time, this is the first track
  if (globalStartTime === null) {
    // Schedule start time in the future to ensure all tracks can sync
    return {
      offset: 0,
      adjustedStartTime: currentTime + 0.2 // 200ms delay for synchronization
    };
  }

  // Calculate one measure duration in seconds
  const effectiveTimeSignature = timeSignature === trackTimeSignature ? timeSignature : 1;
  const oneMeasure = (60 * effectiveTimeSignature) / playbackTempo;

  // Quantize to measure boundaries
  const elapsedTime = currentTime - globalStartTime;
  const elapsedMeasures = Math.floor(elapsedTime / oneMeasure);
  const adjustedStartTime = globalStartTime + elapsedMeasures * oneMeasure;
  const offset = currentTime - adjustedStartTime;

  return {
    offset,
    adjustedStartTime
  };
}

/**
 * Calculate tempo-adjusted offset for playback
 * @param offset - Time offset in seconds
 * @param playbackTempo - Current playback tempo
 * @param originalTempo - Original track tempo
 * @returns Tempo-adjusted offset
 */
export function calculateTempoAdjustedOffset(
  offset: number,
  playbackTempo: number,
  originalTempo: number
): number {
  return (offset * playbackTempo) / originalTempo;
}

/**
 * Validate play options before playback
 * @param options - Play options to validate
 * @returns Validation result with any errors
 */
export function validatePlayOptions(options: {
  url?: string;
  timeSignature?: number | null;
}): PlayOptionsValidation {
  const errors: string[] = [];

  // URL is required
  if (!options.url || typeof options.url !== 'string' || options.url.trim() === '') {
    errors.push('URL is required and must be a non-empty string');
  }

  // If timeSignature is provided, it must be a valid value
  if (
    options.timeSignature !== null &&
    options.timeSignature !== undefined &&
    (typeof options.timeSignature !== 'number' ||
      options.timeSignature <= 0 ||
      !Number.isInteger(options.timeSignature))
  ) {
    errors.push('timeSignature must be null or a positive integer');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Calculate buffer size in frames (aligned to 1024 frame boundaries)
 * @param bufferSizeInSeconds - Desired buffer size in seconds
 * @param sampleRate - Audio sample rate
 * @returns Buffer size in frames, aligned to 1024 boundaries
 */
export function calculateAlignedBufferSize(
  bufferSizeInSeconds: number,
  sampleRate: number
): number {
  const frameSize = 1024;
  const totalSamples = bufferSizeInSeconds * sampleRate;
  return Math.ceil(totalSamples / frameSize) * frameSize;
}

/**
 * Convert beats to samples
 * @param beats - Number of beats
 * @param tempo - Tempo in BPM
 * @param sampleRate - Audio sample rate
 * @returns Number of samples
 */
export function beatsToSamples(beats: number, tempo: number, sampleRate: number): number {
  const secondsPerBeat = 60 / tempo;
  const totalSeconds = beats * secondsPerBeat;
  return Math.round(totalSeconds * sampleRate);
}

/**
 * Convert samples to beats
 * @param samples - Number of samples
 * @param tempo - Tempo in BPM
 * @param sampleRate - Audio sample rate
 * @returns Number of beats
 */
export function samplesToBeat(samples: number, tempo: number, sampleRate: number): number {
  const totalSeconds = samples / sampleRate;
  const secondsPerBeat = 60 / tempo;
  return totalSeconds / secondsPerBeat;
}
